import { useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/auth/useAuth'
import { Alert, Button, FormField, Input } from '@/components/ui'
import './auth-page.css'

/**
 * Einladung einlösen.
 *
 * Ablauf: Supabase verschickt die Einladung, der Link fuehrt hierher und die
 * Sitzung besteht bereits (detectSessionInUrl). Hier wird nur noch das Passwort
 * gesetzt und danach der Einladungs-Token gegen accept_invitation getauscht,
 * das Rolle und Bereichszuweisungen anwendet.
 *
 * Der letzte Schritt ist ein Session-Refresh: die Rechte stehen zwar in der
 * Datenbank, aber das gerade ausgestellte Token kennt sie noch nicht.
 */
export function InviteAccept() {
  const { token } = useParams<{ token: string }>()
  const { session, updatePassword, reload } = useAuth()
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (password.length < 12) {
      setError('Das Passwort muss mindestens 12 Zeichen lang sein.')
      return
    }
    if (password !== confirm) {
      setError('Die beiden Passwörter stimmen nicht überein.')
      return
    }
    if (!session) {
      setError(
        'Keine gültige Sitzung. Bitte den Einladungslink aus der E-Mail erneut öffnen — ' +
          'er ist möglicherweise abgelaufen.',
      )
      return
    }

    setBusy(true)

    const { error: passwordError } = await updatePassword(password)
    if (passwordError) {
      setError(passwordError)
      setBusy(false)
      return
    }

    if (token) {
      const { error: acceptError } = await supabase.rpc('accept_invitation', {
        p_token: token,
      })
      if (acceptError) {
        setError(acceptError.message)
        setBusy(false)
        return
      }
    }

    // Rechte sind vergeben, stehen aber noch nicht im Token.
    await reload()
    setBusy(false)
    navigate('/', { replace: true })
  }

  return (
    <div className="auth-page grid-backdrop">
      <div className="auth-page__panel">
        <p className="eyebrow">NEURA Performance</p>
        <h1 className="auth-page__title">Zugang einrichten</h1>
        <p className="auth-page__lead">
          Legen Sie ein Passwort fest. Danach wird Ihr Konto mit den Rechten aus der
          Einladung freigeschaltet.
        </p>

        <form className="auth-page__form" onSubmit={handleSubmit}>
          <FormField
            label="Passwort"
            hint="Mindestens 12 Zeichen. Eine Passphrase aus mehreren Wörtern ist sicherer als ein kurzes Sonderzeichen-Kürzel."
            required
          >
            {(field) => (
              <Input
                {...field}
                type="password"
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </FormField>

          <FormField label="Passwort wiederholen" required>
            {(field) => (
              <Input
                {...field}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            )}
          </FormField>

          {error && <Alert tone="danger">{error}</Alert>}

          <Button type="submit" variant="primary" size="lg" block loading={busy}>
            Zugang aktivieren
          </Button>
        </form>
      </div>
    </div>
  )
}

/** Passwort nach einem Reset-Link neu setzen. */
export function ResetPassword() {
  const { session, updatePassword } = useAuth()
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (password.length < 12) {
      setError('Das Passwort muss mindestens 12 Zeichen lang sein.')
      return
    }
    if (!session) {
      setError('Der Link ist abgelaufen. Bitte fordern Sie das Zurücksetzen erneut an.')
      return
    }

    setBusy(true)
    const { error: updateError } = await updatePassword(password)
    setBusy(false)

    if (updateError) setError(updateError)
    else navigate('/', { replace: true })
  }

  return (
    <div className="auth-page grid-backdrop">
      <div className="auth-page__panel">
        <p className="eyebrow">NEURA Performance</p>
        <h1 className="auth-page__title">Neues Passwort</h1>

        <form className="auth-page__form" onSubmit={handleSubmit}>
          <FormField label="Neues Passwort" hint="Mindestens 12 Zeichen." required>
            {(field) => (
              <Input
                {...field}
                type="password"
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </FormField>

          {error && <Alert tone="danger">{error}</Alert>}

          <Button type="submit" variant="primary" size="lg" block loading={busy}>
            Passwort speichern
          </Button>
        </form>
      </div>
    </div>
  )
}
